import { MigrationWave } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, withFlashContext } from '@amzn/awscat-react-components';
import { Box, Button, Spinner } from '@amzn/awsui-components-react';
import { useEffect, useState } from 'react';
import { TimelineGroup, TimelineItem } from 'vis-timeline';

import { constants } from '../../../common/Localization';
import VisTimeline from './VisTimeline';

type Props = FlashContextInterface & {
    migrationWaves: MigrationWave[];
    loading: boolean;
    addToWave: (groupIds: string[], waveId: string) => Promise<void>;
    removeFromWave: (groupIds: string[]) => Promise<void>;
    openAddWavesDialog: () => void;
};

function MigrationWavesTimeline({loading, migrationWaves, addToWave, removeFromWave, openAddWavesDialog}: Props) {
    const [ groupsDataSet, setGroupsDataSet ] = useState<TimelineItem[]>([]);
    const [ wavesDataSet, setWavesDataSet ] = useState<TimelineGroup[]>([]);
    const [ firstWave, setFirstWave ] = useState<MigrationWave>();
    const [ currentWave, setCurrentWave ] = useState<MigrationWave>();
    useEffect(() => {
        const groups: TimelineItem[] = [];
        const backgrounds: TimelineItem[] = [];
        let earliestWave: MigrationWave = migrationWaves[0] || null;
        let currWave: MigrationWave | undefined;
        const date = new Date();
        const waves: TimelineGroup[] = migrationWaves.map(wave => {
            // Find earliest wave
            if (!earliestWave || earliestWave.startDate > wave.startDate) {
                earliestWave = wave;
            }
            // Find the first current wave that spans current DateTime
            if (!currWave && wave.startDate <= date && wave.endDate >= date) {
                currWave = wave;
            }
            // Generate background and items from ApplicationGroups; generate groups from MigrationWaves
            const groupsInWave: TimelineItem[] = wave.groups?.length === 0 ? [] :
                wave.groups.map(group => {
                    const waveTitle = !!wave.name ? `${group.name} - ${wave.name}<br>${group.avgDataCollectedPct}% ${constants.PASSED_VALIDATION}`: group.name;
                    return {
                        id: group.applicationGroupId,
                        content: waveTitle,
                        start: wave.startDate,
                        end: wave.endDate,
                        group: wave.migrationWaveId,
                        title: waveTitle,
                        value: group.name,
                    };
                });
            const backgroundItem: TimelineItem = {
                id: `bg-${wave.migrationWaveId}`,
                start: wave.startDate,
                end: wave.endDate,
                type: 'background',
                group: wave.migrationWaveId,
                content: '',
            } as TimelineItem;
            backgrounds.push(backgroundItem);
            groups.push(...groupsInWave);
            return {
                id: wave.migrationWaveId,
                content: wave.name,
                value: wave.startDate,
            };
        });
        setCurrentWave(currWave);
        setFirstWave(earliestWave);
        setWavesDataSet(waves);
        setGroupsDataSet(backgrounds.concat(groups));
    }, [migrationWaves]);
    const landingPage = (
        <Box textAlign='center' className='landing-container'>
            <h1>{constants.WAVE_PLANNING_TITLE}</h1>
            <p>
                {constants.NO_WAVES_MESSAGE_1}
            </p>
            <p>
                {constants.NO_WAVES_MESSAGE_2}
                <b>{constants.GET_STARTED_BUTTON_TEXT}</b>
                {constants.NO_WAVES_MESSAGE_3}
            </p>
            <Box padding={{top: 'xl'}}>
                <Button variant='primary' onClick={openAddWavesDialog} id='btn-getstarted-wave'>
                    {constants.GET_STARTED_BUTTON_TEXT}
                </Button>
            </Box>
        </Box>
    );
    const first = () => firstWave && `bg-${firstWave.migrationWaveId}` || '';
    const current = () => currentWave && `bg-${currentWave.migrationWaveId}` || '';
    if (loading) {
        return <Spinner size='big' />;
    } else if (!migrationWaves.length) {
        return landingPage;
    } else {
        return (
            <VisTimeline
                groups={wavesDataSet}
                items={groupsDataSet}
                setItems={setGroupsDataSet}
                first={first}
                current={current}
                onAdd={addToWave}
                onRemove={removeFromWave}/>
        );
    }
}

export default withFlashContext(MigrationWavesTimeline);
